<template>
  <div id="leaderShipCards" class="leadership-grid">
    <article
      v-for="person in dataSorted"
      :key="person.firstName + person.lastName"
      class="card"
      @click="onClick(person)"
    >
      <!-- Image / avatar area -->
      <div class="card-hero">
        <img :src="resolveImage(person.image)" width="350" />
      </div>

      <!-- Info section -->
      <div class="card-body">
        <p class="role">{{ person.title }}</p>
        <h3 class="name">{{ person.firstName }} {{ person.lastName }}</h3>

        <div class="cta-wrap">
          <font-awesome-icon :icon="faChevronRight" v-if="person.linkedIn" />
          <span class="cta">Get to Know {{ person.firstName }}</span>
        </div>
      </div>
    </article>
  </div>
</template>

<style scoped>
/* ---------- Theme variables (tweak as needed) ---------- */
:root {
  --pgptitle-navy: #083a5b; /* dark hero background */
  --border: #e6e6e6;
  --shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  --text-weak: #6b7280; /* muted gray */
  --text-strong: #0f172a; /* near-black */
  --teal: #0ea5a4; /* link color */
  --radius-lg: 16px;
  --radius-md: 12px;
  --gap: 2rem;
}

/* ---------- Responsive grid: 1 / 2 / 3 columns ---------- */
.leadership-grid {
  margin: 48px auto;
  display: grid;
  gap: var(--gap, 1rem);
  grid-template-columns: 1fr; /* mobile: 1 */
  max-width: 1200px;
  padding: 0 1rem;
}

/* laptop/tablet: 2 */
@media (min-width: 768px) {
  .leadership-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
/* desktop: 3 */
@media (min-width: 1200px) {
  .leadership-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* ---------- Card ---------- */
.card {
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: #fff;
  box-shadow: var(--shadow);
  border-radius: 1.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  max-width: 325px;
  border: 2px solid #fafafa;
  transition: box-shadow 0.2s ease, transform 0.1s ease, border-color 0.2s ease;
}

.card:hover {
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
  cursor: pointer;
  border-color: #3072a4;
}

/* Top image area with navy background and fixed aspect */
.card-hero {
  position: relative;
  aspect-ratio: 4 / 5; /*  keeps tall hero area like screenshot */
  /* min-height: 240px; */
}

/* Round top corners visually */
.card-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  border-top-left-radius: var(--radius-lg);
  border-top-right-radius: var(--radius-lg);
}

/* Image fills the hero area; hidden if not available (SVG placeholder shows) */
.card-hero img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Body area */

.card-body {
  display: flex;
  flex-direction: column;
  padding: 1rem 1.25rem 1.25rem;
  background: #fff;
  gap: 0.5rem; /* optional spacing between role/name and CTA */
  height: 100%;
}

.role {
  margin: 0 0 0.25rem 0;
  color: var(--text-weak);
  font-size: 1.3rem;
  font-weight: 300;
}

.name {
  flex-grow: 1;
  margin: 0 0 0.5rem 0;
  color: #083a5b;
  font-weight: 800;
  font-size: 1.9rem;
  letter-spacing: 0.2px;
}

.cta-wrap {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: auto; /* <-- pushes this block to the bottom */
}

.cta {
  color: #018181;
  text-decoration: underline;
  font-weight: 600;
  font-size: 1.4rem;
}
.cta:hover,
.cta:focus {
  text-decoration: underline;
}

.card-body > .cta-wrap > svg {
  color: #018181;
  font-weight: bolder;
  font-size: 1.3rem;
}
</style>

<script setup>
import { computed } from "vue";
import data from "../data/leadership.json";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";

const emit = defineEmits(["select"]);

const dataSorted = computed(() => {
  return [...data].sort((a, b) => a.index - b.index);
});

const resolveImage = (fileName) => {
  fileName = fileName ? fileName : "NotProvided.png";
  return require(`@/assets/images/leadership/${fileName}`);
};

const onClick = (person) => {
  emit("select", { ...person });
};
</script>
